import {
  Button,
  Dialog,
  DialogClose,
  InterceptDialog,
  Radio,
  Tooltip,
  Typography,
} from "@hellocarbo/lasagne";
import { Download, LoaderCircle } from "lucide-react";
import { use, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import Folder from "@/assets/common/other_icons/folder.svg?react";
import { PortalContainerContext, UserContext } from "@/contexts";
import useOutboundQueryParams from "@/hooks/useOutboundQueryParams";
import useSegmentEvent from "@/hooks/useSegmentEvent";

import BaseCard from "./BaseCard";

function DownloadFootprintAnalysis({
  reviewId,
  companyHasSubsidiaries,
  hasOtherReviewsAtSameDate,
  isMultientityDashboard = false,
  disabled = false,
  disabledTooltip,
}: {
  reviewId?: number;
  companyHasSubsidiaries?: boolean;
  hasOtherReviewsAtSameDate?: boolean;
  isMultientityDashboard?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
}) {
  const { t } = useTranslation(["communicate", "dashboard"]);
  const { sendSegmentEvent } = useSegmentEvent();

  const [template, setTemplate] = useState<
    "fr" | "en" | "fr_consolidated" | "en_consolidated"
  >(isMultientityDashboard ? "fr_consolidated" : "fr");
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);

  const freeTrialQueryParams = useOutboundQueryParams("freetrial");
  const upgradeQueryParams = useOutboundQueryParams("upgrade");

  const { container } = use(PortalContainerContext);
  const { user } = use(UserContext);

  const canExportPdfRestitution =
    user?.policies?.pdf_restitution_export.queries["index"];
  const isFreeTrial = user?.company.subscriptionType === "start";

  const multientityTriggerLabel = isMultientityDashboard
    ? t("dashboard:analysisAndCommunication.downloadReport")
    : undefined;

  const freetrialFooter = (
    <>
      <DialogClose asChild>
        <Button
          shape="link"
          as={Link}
          to="/pricing"
          onClick={() => {
            sendSegmentEvent("Discover the plans", {
              size: `${user?.company.employees}`,
            });
          }}
        >
          {t("communicate:freeTrial.modal.secondaryAction")}
        </Button>
      </DialogClose>
      <DialogClose asChild>
        <Button
          as="a"
          href={`https://www.hellocarbo.com/reservez-votre-demo${freeTrialQueryParams}`}
          onClick={() => sendSegmentEvent("Subscription Asked")}
          shouldOpenNewTab
        >
          {t("communicate:freeTrial.modal.primaryAction")}
        </Button>
      </DialogClose>
    </>
  );

  const upgradeFooter = (
    <DialogClose>
      <Button
        as="a"
        href={`https://try.hellocarbo.com/meetings/emmanuel-watrinet/rdv-abonnement-carbo-entreprise${upgradeQueryParams}`}
        shouldOpenNewTab
        onClick={() => sendSegmentEvent("clic_CTA_upsell")}
      >
        {t("communicate:downloadFootprintAnalysis.modal.cta")}
      </Button>
    </DialogClose>
  );

  const handleDownload = async () => {
    setShowLoadingOverlay(true);

    const isConsolidated = template.includes("consolidated");
    const languageParam = template.replace("_consolidated", "");

    const endpoint = isConsolidated
      ? `/api/client/reviews/${reviewId}/generate_consolidated_report?language=${languageParam}`
      : `/api/client/reviews/${reviewId}/generate_report?language=${languageParam}`;

    const response = await fetch(endpoint, {
      method: "POST",
    });

    const data = await response.json();
    const url = languageParam === "fr" ? data.fr_url : data.en_url;

    if (url) {
      sendSegmentEvent("pdf_restitution_downloaded");
      window.open(url, "_blank");
      setShowLoadingOverlay(false);
      return;
    }

    if (data.enqueued) {
      const checkCertificate = async () => {
        const checkResponse = await fetch(endpoint, { method: "POST" });
        const checkData = await checkResponse.json();
        const checkUrl =
          languageParam === "fr" ? checkData.fr_url : checkData.en_url;

        if (checkUrl) {
          window.open(checkUrl, "_blank");
          setShowLoadingOverlay(false);
        } else {
          setTimeout(() => void checkCertificate(), 2000);
        }
      };
      sendSegmentEvent("pdf_restitution_downloaded");
      checkCertificate();
    }
  };

  const disabledButton = (
    <div className="mt-xs">
      <Button shape="soft" intent="secondary" disabled>
        <Download />
        {multientityTriggerLabel ??
          t("communicate:downloadFootprintAnalysis.cta")}
      </Button>
    </div>
  );

  const content = disabled ? (
    disabledTooltip ? (
      <Tooltip
        portalContainer={container as Element}
        side="top"
        content={disabledTooltip}
        triggerAsChild
      >
        {disabledButton}
      </Tooltip>
    ) : (
      disabledButton
    )
  ) : (
    <>
      {!canExportPdfRestitution ? (
        <InterceptDialog
          show={true}
          title={
            isFreeTrial
              ? t("communicate:freeTrial.modal.title")
              : t("communicate:downloadFootprintAnalysis.modal.title")
          }
          description={
            isFreeTrial
              ? undefined
              : t("communicate:downloadFootprintAnalysis.modal.description")
          }
          warningMessage={
            isFreeTrial
              ? t("communicate:freeTrial.modal.warning")
              : t("communicate:downloadFootprintAnalysis.modal.warning")
          }
          portalContainer={container as Element}
          content={
            isFreeTrial && (
              <Typography>
                {t("communicate:freeTrial.modal.content")}
              </Typography>
            )
          }
          footer={isFreeTrial ? freetrialFooter : upgradeFooter}
        >
          <div className="mt-xs">
            <Button
              shape="soft"
              intent="secondary"
              as={Link}
              to="#"
              role="link"
            >
              <Download />
              {multientityTriggerLabel ??
                t("communicate:downloadFootprintAnalysis.cta")}
            </Button>
          </div>
        </InterceptDialog>
      ) : (
        <>
          <Dialog
            title={t(
              "communicate:downloadFootprintAnalysis.report_modal.title"
            )}
            description={t(
              "communicate:downloadFootprintAnalysis.report_modal.description"
            )}
            portalContainer={container as Element}
            trigger={
              <div className="mt-xs">
                <Button shape="soft" intent="secondary">
                  <Download />
                  {multientityTriggerLabel ??
                    t(
                      "communicate:downloadFootprintAnalysis.report_modal.trigger"
                    )}
                </Button>
              </div>
            }
            footer={
              <>
                <DialogClose asChild>
                  <Button shape="link">
                    {t(
                      "communicate:downloadFootprintAnalysis.report_modal.cancel"
                    )}
                  </Button>
                </DialogClose>
                <DialogClose asChild>
                  <Button shape="solid" onClick={handleDownload}>
                    {t(
                      "communicate:downloadFootprintAnalysis.report_modal.validate"
                    )}
                  </Button>
                </DialogClose>
              </>
            }
          >
            <Radio
              type="button"
              name="report_language"
              label={t(
                "communicate:downloadFootprintAnalysis.report_modal.radio_label"
              )}
              defaultValue={template}
              items={[
                ...(isMultientityDashboard
                  ? []
                  : [
                      {
                        label: t(
                          "communicate:downloadFootprintAnalysis.report_modal.fr_simple_label"
                        ),
                        value: "fr",
                      },
                      {
                        label: t(
                          "communicate:downloadFootprintAnalysis.report_modal.en_simple_label"
                        ),
                        value: "en",
                      },
                    ]),
                ...(isMultientityDashboard ||
                (companyHasSubsidiaries && hasOtherReviewsAtSameDate)
                  ? [
                      {
                        label: t(
                          "communicate:downloadFootprintAnalysis.report_modal.fr_consolidated_label"
                        ),
                        value: "fr_consolidated",
                      },
                      {
                        label: t(
                          "communicate:downloadFootprintAnalysis.report_modal.en_consolidated_label"
                        ),
                        value: "en_consolidated",
                      },
                    ]
                  : []),
              ]}
              onValueChange={(value: unknown) => {
                if (
                  typeof value === "string" &&
                  ["fr", "en", "fr_consolidated", "en_consolidated"].includes(
                    value
                  )
                ) {
                  setTemplate(
                    value as "fr" | "en" | "fr_consolidated" | "en_consolidated"
                  );
                }
              }}
            />
          </Dialog>
          {showLoadingOverlay && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <div className="gap-md p-lg flex flex-col items-center rounded-lg bg-white shadow-lg">
                <LoaderCircle className="text-primary h-10 w-10 animate-spin" />
                <p className="text-center">{t("common:loading")}</p>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );

  if (isMultientityDashboard) {
    return content;
  }

  return (
    <BaseCard
      title={t("downloadFootprintAnalysis.title")}
      text={t("downloadFootprintAnalysis.text")}
      Icon={Folder}
    >
      {content}
    </BaseCard>
  );
}

export default DownloadFootprintAnalysis;
